/**
 * AdminLicensesPage — License management per organization.
 */

import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { RefreshCw } from 'lucide-react';
import { AdminConsoleService } from '../../../services/adminConsoleService';
import { RequireCapability } from '../../../components/auth/RequireCapability';
import { AdminConsoleLayout } from '../components/AdminConsoleLayout';
import { ModuleCard, ModuleLoadingState } from '../../shared/components/ModulePageHeader';
import { EnterpriseStatusBadge } from '../../shared/components/EnterpriseStatusBadge';
import { normalizeOrganizationStatus } from '../../org/utils/normalizeOrganizationStatus';
import type { LicenseStatus } from '../../../types/adminConsole';

const currency = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });

const STATUS_OPTIONS: { value: LicenseStatus; label: string }[] = [
  { value: 'active', label: 'Ativo' },
  { value: 'trial', label: 'Trial' },
  { value: 'suspended', label: 'Suspenso' },
  { value: 'expired', label: 'Expirado' },
];

const planBadge: Record<string, string> = {
  enterprise: 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950/40 dark:text-blue-300 dark:border-blue-900/60',
  professional: 'bg-indigo-50 text-indigo-700 border-indigo-200 dark:bg-indigo-950/40 dark:text-indigo-300 dark:border-indigo-900/60',
  starter: 'bg-slate-100 text-slate-700 border-slate-200 dark:bg-zinc-800 dark:text-zinc-300 dark:border-zinc-700',
};

interface AdminLicensesPageProps {
  externalSearch?: string;
  externalStatusFilter?: string;
}

export const AdminLicensesPage: React.FC<AdminLicensesPageProps> = ({ externalSearch, externalStatusFilter }) => {
  const qc = useQueryClient();
  const searchTerm = externalSearch ?? '';
  const statusFilter = externalStatusFilter ?? 'all';

  const licensesQuery = useQuery({
    queryKey: ['admin-licenses'],
    queryFn: () => AdminConsoleService.getLicenses(),
  });

  const statusMutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: LicenseStatus }) =>
      AdminConsoleService.updateLicenseStatus(id, status),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-licenses'] }),
  });

  const filtered = (licensesQuery.data ?? []).filter((lic) => {
    const matchSearch = !searchTerm || (lic.organization_name ?? '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchStatus = statusFilter === 'all' || lic.status === statusFilter;
    return matchSearch && matchStatus;
  });

  return (
    <RequireCapability capability="admin.access" realRoleOnly>
      <AdminConsoleLayout>
        <div className="enterprise-page h-full overflow-y-auto">

          {licensesQuery.isPending && <ModuleLoadingState label="Carregando licenças" />}

          {!licensesQuery.isPending && (
            <ModuleCard
              title={`Licenças (${filtered.length})`}
            >
              {!filtered.length ? (
                <p className="text-[12px] enterprise-text-secondary py-4 text-center">Nenhuma licença encontrada.</p>
              ) : (
                <div className="enterprise-table-shell">
                  <table className="w-full text-left text-sm">
                    <thead className="enterprise-table-header">
                      <tr>
                        <th className="enterprise-table-head-cell">Organização</th>
                        <th className="enterprise-table-head-cell">Plano</th>
                        <th className="enterprise-table-head-cell">Status</th>
                        <th className="enterprise-table-head-cell text-right">Usuários</th>
                        <th className="enterprise-table-head-cell text-right">MRR</th>
                        <th className="enterprise-table-head-cell">Expira em</th>
                        <th className="enterprise-table-head-cell">Ação</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filtered.map((lic) => (
                        <tr key={lic.id} className="enterprise-table-row">
                          <td className="enterprise-table-cell font-medium">{lic.organization_name || lic.organization_id}</td>
                          <td className="enterprise-table-cell">
                            <span className={`border rounded px-1.5 py-0.5 text-[11px] font-medium ${planBadge[lic.plan] ?? planBadge.starter}`}>
                              {(lic.plan ?? '').toUpperCase()}
                            </span>
                          </td>
                          <td className="enterprise-table-cell">
                            <EnterpriseStatusBadge
                              status={normalizeOrganizationStatus(lic.status)}
                              label={lic.status?.toUpperCase() ?? '—'}
                            />
                          </td>
                          <td className="enterprise-table-cell text-right tabular-nums">
                            {lic.active_users} / {lic.max_users}
                          </td>
                          <td className="enterprise-table-cell text-right tabular-nums font-mono">
                            {currency.format(lic.monthly_value ?? 0)}
                          </td>
                          <td className="enterprise-table-cell enterprise-text-secondary">
                            {lic.expires_at ? new Date(lic.expires_at).toLocaleDateString('pt-BR') : '—'}
                          </td>
                          <td className="enterprise-table-cell">
                            <select
                              className="enterprise-input text-[11px] py-0.5"
                              value={lic.status}
                              onChange={(e) => statusMutation.mutate({ id: lic.id, status: e.target.value as LicenseStatus })}
                              style={{ width: '100px' }}
                            >
                              {STATUS_OPTIONS.map((s) => (
                                <option key={s.value} value={s.value}>{s.label}</option>
                              ))}
                            </select>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </ModuleCard>
          )}
        </div>
      </AdminConsoleLayout>
    </RequireCapability>
  );
};
